import React from 'react';
import Reveal from './Reveal';
import { useBoardData } from '../../hooks/useBoard';
import { motion } from 'framer-motion';
import { Star, ShieldCheck, Users } from 'lucide-react';

const Board: React.FC = () => {
    const { data, isLoading } = useBoardData();

    if (isLoading) {
        return (
            <div className="py-32 flex flex-col items-center justify-center bg-white text-slate-400">
                <div className="w-12 h-12 border-t-2 border-brand-green border-r-2 rounded-full animate-spin mb-4"></div>
                <p className="font-medium tracking-widest uppercase text-xs">Carregando Diretoria...</p>
            </div>
        );
    }

    if (!data) return null;

    const { members: boardMembers, support } = data;

    const isSuplente = (role: string) => role.toLowerCase().includes('supl');

    const titulares = boardMembers.filter(m => !isSuplente(m.role));
    
    const substitutes = [
        ...boardMembers.filter(m => isSuplente(m.role)).map(m => ({ ...m, type: 'board' })),
        ...support.filter(s => isSuplente(s.role)).map(s => ({ ...s, type: 'support' }))
    ];

    const otherSupport = support.filter(s => !isSuplente(s.role));

    return (
        <section className="py-24 bg-slate-50 relative overflow-hidden" id="diretoria">
            {/* Background Texture - Light & Subtle */}
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/diagmonds-light.png')" }}></div>
            
            {/* Soft decorative gradients */}
            <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand-green/5 blur-[100px] rounded-full pointer-events-none"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[30%] h-[30%] bg-blue-500/5 blur-[80px] rounded-full pointer-events-none"></div>

            <div className="container mx-auto px-4 relative z-10">
                <Reveal>
                    <div className="text-center mb-20">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <ShieldCheck className="text-brand-green" size={20} />
                            <span className="text-brand-green font-bold tracking-[0.2em] uppercase text-xs">
                                Liderança ACONTURS
                            </span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black text-brand-darkGreen mt-2 leading-tight">
                            Diretoria Regional
                        </h2>
                        <div className="w-24 h-1.5 bg-brand-yellow mx-auto mt-6 rounded-full"></div>
                        <p className="text-slate-500 mt-6 max-w-xl mx-auto text-lg leading-relaxed font-medium opacity-80">
                            Representação oficial dos Conselheiros Tutelares do Litoral Norte para a gestão atual.
                        </p>
                    </div>
                </Reveal>

                {/* Titulares Grid - Clean Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-24 max-w-7xl mx-auto">
                    {titulares.map((member, idx) => (
                        <Reveal key={member.id} delay={idx * 150}>
                            <motion.div 
                                whileHover={{ y: -12 }}
                                className="bg-white rounded-[2rem] p-8 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-col items-center text-center h-full transition-all duration-300 hover:shadow-[0_30px_60px_rgba(24,113,63,0.12)] group"
                            >
                                {/* Member Image Container */}
                                <div className="relative mb-8 pt-2">
                                    <div className="w-44 h-44 rounded-[2.5rem] p-1.5 bg-gradient-to-tr from-brand-green/20 to-brand-green/5 shadow-inner relative overflow-hidden transform group-hover:scale-105 transition-transform duration-500">
                                        <img 
                                            src={member.image} 
                                            alt={member.name} 
                                            className="w-full h-full rounded-[2rem] object-cover" 
                                        />
                                    </div>
                                    {/* Position Badge */}
                                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white px-4 py-1.5 rounded-full shadow-lg border border-slate-50 flex items-center gap-2">
                                        <Star size={14} className="text-brand-yellow fill-brand-yellow" />
                                        <span className="text-brand-darkGreen font-black text-[10px] uppercase tracking-wider">{member.role}</span>
                                    </div>
                                </div>

                                <h3 className="text-2xl font-black text-slate-800 mb-4 mt-2">{member.name}</h3>
                                
                                <p className="text-slate-500 text-sm leading-relaxed mb-8 flex-grow font-medium italic px-4">
                                    "{member.description}"
                                </p>

                                {/* Tags - Modern Badges */}
                                <div className="flex gap-2 justify-center flex-wrap pt-8 border-t border-slate-100 w-full">
                                    {member.tags.map((tag, i) => (
                                        <span 
                                            key={i} 
                                            className="px-3 py-1.5 rounded-lg bg-slate-50 text-slate-500 text-[9px] font-bold border border-slate-100 uppercase tracking-widest"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>

                {/* Suplência Section - Elegant Horizontal Layout */}
                {substitutes.length > 0 && (
                    <Reveal delay={300}>
                        <div className="mt-20 pt-20 border-t border-slate-200/60">
                            <div className="text-center mb-16">
                                <h3 className="text-slate-400 font-black text-xl uppercase tracking-[0.4em] mb-4">
                                    Suplência
                                </h3>
                                <div className="flex justify-center gap-1">
                                    {[1, 2, 3].map(i => <div key={i} className="w-1.5 h-1.5 bg-brand-green/30 rounded-full"></div>)}
                                </div>
                            </div>
                            
                            <div className="flex flex-wrap justify-center gap-x-12 gap-y-16 max-w-6xl mx-auto">
                                {substitutes.map((suplente) => (
                                    <div 
                                        key={suplente.id} 
                                        className="flex flex-col items-center text-center group w-40"
                                    >
                                        <div className="relative mb-5">
                                            <div className="w-28 h-28 md:w-32 md:h-32 rounded-[2rem] p-1 bg-white shadow-md group-hover:shadow-brand-green/20 transition-all duration-500 overflow-hidden">
                                                <img 
                                                    src={suplente.image} 
                                                    alt={suplente.name} 
                                                    className="w-full h-full rounded-[1.8rem] object-cover grayscale group-hover:grayscale-0 transition-all duration-500 scale-105 group-hover:scale-100" 
                                                />
                                            </div>
                                        </div>
                                        <h4 className="font-bold text-slate-800 text-base leading-tight">{suplente.name}</h4>
                                        <span className="text-[10px] font-black text-brand-green uppercase mt-2 block tracking-widest">{suplente.role}</span>
                                        <p className="text-[10px] text-slate-400 mt-2 block leading-relaxed max-w-[140px] mx-auto italic font-medium">{(suplente as any).description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                )}

                {/* Outros Membros / Apoio Técnico - Professional List */}
                {otherSupport.length > 0 && (
                    <Reveal delay={400}>
                        <div className="mt-24 pt-20 border-t border-slate-200/60">
                            <div className="flex items-center gap-4 mb-12 justify-center">
                                <Users size={18} className="text-slate-300" />
                                <h3 className="text-slate-400 font-bold text-sm uppercase tracking-[0.2em]">
                                    Apoio & Assessoria
                                </h3>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                                {otherSupport.map((member) => (
                                    <div 
                                        key={member.id} 
                                        className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-slate-100 hover:border-brand-green/30 transition-colors shadow-sm"
                                    >
                                        <div className="w-14 h-14 rounded-xl overflow-hidden shadow-inner flex-shrink-0 bg-slate-50">
                                            <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="text-left">
                                            <h4 className="text-slate-800 font-bold text-xs leading-tight">{member.name}</h4>
                                            <p className="text-brand-green text-[9px] uppercase font-black tracking-widest mt-1">{member.role}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    );
};

export default Board;